import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HelperProfile, WorkExperience } from '../types';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { useDataDictionaries, DATA_DICTIONARY_TYPES } from '../hooks/useDataDictionary';
import { getDictDisplayValue, getDictValueByCode, resolveApiError } from '../utils/i18n';
import { markHelperViewed } from '../utils/viewedStore';
import { latestDate, formatRelativeTime, calculateAge, formatYearMonth, calculateDuration as calcDuration, parseServerDate } from '../utils/date';
import { useAlert } from '../contexts/AlertContext';
import {
  Calendar, Check, MapPin, Edit, User, Briefcase, ArrowLeft,
  Globe, MessageCircle, Home, Users, Lock,
  Activity, Clock, Award, GraduationCap, AlertCircle,
  Baby, Milk, Smile, Accessibility, Dog,
  Send, Crown, Share2, Bookmark, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CookingProficiencyTooltip } from '../components/CookingProficiencyTooltip';
import { useTranslation } from 'react-i18next';
import { getAccommodationOptions } from '../constants/formOptions';

const HelperDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [helper, setHelper] = useState<HelperProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [intent, setIntent] = useState<any | null>(null);
  const [sendingIntent, setSendingIntent] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [mismatchWarning, setMismatchWarning] = useState<string | null>(null);

  const parentCodes = [
    DATA_DICTIONARY_TYPES.NATIONALITY,
    DATA_DICTIONARY_TYPES.MARITAL_STATUS,
    DATA_DICTIONARY_TYPES.GENDER,
    DATA_DICTIONARY_TYPES.RELIGION,
    DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE,
    DATA_DICTIONARY_TYPES.HOUSEWORK,
    DATA_DICTIONARY_TYPES.COOKING_SKILLS,
    DATA_DICTIONARY_TYPES.OTHER_SKILLS,
    DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS,
    DATA_DICTIONARY_TYPES.EDUCATION_LEVEL,
    DATA_DICTIONARY_TYPES.JOB_LOCATION,
    DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL,
    DATA_DICTIONARY_TYPES.LANGUAGE_PROFICIENCY,
  ];

  const { dictionariesByParent } = useDataDictionaries({ parentCodes });

  const nationalityDicts    = dictionariesByParent.get(DATA_DICTIONARY_TYPES.NATIONALITY) || [];
  const maritalStatusDicts  = dictionariesByParent.get(DATA_DICTIONARY_TYPES.MARITAL_STATUS) || [];
  const genderDicts         = dictionariesByParent.get(DATA_DICTIONARY_TYPES.GENDER) || [];
  const religionDicts       = dictionariesByParent.get(DATA_DICTIONARY_TYPES.RELIGION) || [];
  const careDicts           = dictionariesByParent.get(DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE) || [];
  const houseworkDicts      = dictionariesByParent.get(DATA_DICTIONARY_TYPES.HOUSEWORK) || [];
  const cookingDicts        = dictionariesByParent.get(DATA_DICTIONARY_TYPES.COOKING_SKILLS) || [];
  const otherSkillsDicts    = dictionariesByParent.get(DATA_DICTIONARY_TYPES.OTHER_SKILLS) || [];
  const languageDicts       = dictionariesByParent.get(DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS) || [];
  const proficiencyDicts    = dictionariesByParent.get(DATA_DICTIONARY_TYPES.LANGUAGE_PROFICIENCY) || [];
  const educationLevelDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.EDUCATION_LEVEL) || [];
  const workExpSkillDicts   = dictionariesByParent.get(DATA_DICTIONARY_TYPES.WORK_EXPERIENCE_SKILL) || [];
  const locationDicts       = dictionariesByParent.get(DATA_DICTIONARY_TYPES.JOB_LOCATION) || [];

  useEffect(() => {
    if (id) {
      loadHelper();
      if (user?.role === 'EMPLOYER' && user?.id) {
        checkIntent();
        checkBookmark();
      }
      recordView();
      markHelperViewed(parseInt(id));
    }
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authLoading && user?.role === 'HELPER') {
    return <Navigate to="/job-postings" replace />;
  }

  const recordView = async () => {
    if (!id) return;
    try {
      await apiService.statistics.recordHelperProfileView(parseInt(id), user?.id, user?.role || 'ANONYMOUS');
    } catch {}
  };

  const checkBookmark = async () => {
    if (!user?.id || !id) return;
    try {
      const response = await apiService.candidateBookmarks.check(parseInt(id));
      if (response.success) setIsBookmarked(response.data);
    } catch {}
  };

  const checkIntent = async () => {
    if (!user?.id || !id) return;
    try {
      const res: any = await apiService.interviewIntents.check(user.id, parseInt(id));
      if (res.success) setIntent(res.data ?? null);
    } catch {}
  };

  const handleSendIntent = async () => {
    if (!user?.id || !id) return;
    setSendingIntent(true);
    try {
      const res: any = await apiService.interviewIntents.send(user.id, parseInt(id));
      if (res.success) {
        setIntent(res.data);
        showAlert({ type: 'success', message: t('helperDetail.intentSent', '面試意向已發送！等待外傭回覆。') });
      } else {
        showAlert({ type: 'error', message: resolveApiError(res.errorCode, res.message, t) });
      }
    } catch (err: any) {
      showAlert({ type: 'error', message: resolveApiError(err?.response?.data?.errorCode, err?.response?.data?.message, t) || t('helperDetail.sendFailedRetry') });
    } finally {
      setSendingIntent(false);
    }
  };

  const doContactHelper = () => {
    if (!helper) return;
    const phone = helper.whatsappPhone?.replace(/\D/g, '');
    if (phone) {
      window.open(`https://wa.me/${phone}`, '_blank');
    } else {
      showAlert({ type: 'error', message: t('helperDetail.noWhatsapp', '外傭未提供 WhatsApp 號碼') });
    }
  };

  const handleContactHelper = async () => {
    if (!helper) return;
    if (!user?.isVip) {
      navigate('/subscription');
      return;
    }
    try {
      const res = await apiService.helpers.mismatchCheck(helper.id, user.id);
      if (res.data?.mismatch) {
        const msgKey = res.data.messageKey;
        const msg = msgKey ? t(msgKey, { defaultValue: t('helperDetail.mismatchTitle') }) : t('helperDetail.mismatchTitle');
        setMismatchWarning(msg);
        return;
      }
    } catch {}
    doContactHelper();
  };

  const handleToggleBookmark = async () => {
    if (!user) {
      sessionStorage.setItem('returnUrl', window.location.pathname);
      navigate('/login-form');
      return;
    }
    if (user.role !== 'EMPLOYER' || !id) return;
    try {
      if (isBookmarked) {
        await apiService.candidateBookmarks.remove(parseInt(id));
        setIsBookmarked(false);
      } else {
        await apiService.candidateBookmarks.add(parseInt(id));
        setIsBookmarked(true);
      }
    } catch {}
  };

  const loadHelper = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiService.helpers.getById(parseInt(id));
      if (response.success && response.data) setHelper(response.data);
    } catch {
      console.error('Error loading helper');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loading message={t('common.loading')} />
      </div>
    );
  }

  if (!helper) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <AlertCircle className="w-16 h-16 text-red-500" />
        <div className="text-xl font-semibold text-foreground">{t('helperDetail.notFound')}</div>
        <Button onClick={() => navigate('/candidates')}>{t('helperDetail.backHome')}</Button>
      </div>
    );
  }

  const isOwnProfileCheck = user?.id === helper.userId;
  const isAdminCheck = (user?.role as string) === 'ADMIN' || (user?.role as string) === 'SUPER_ADMIN';
  if (!helper.lookingForJob && !isOwnProfileCheck && !isAdminCheck) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 text-center px-4">
        <AlertCircle className="w-16 h-16 text-muted-foreground" />
        <div className="text-xl font-semibold text-foreground">{t('helperDetail.unavailable', '此用戶目前沒有開放')}</div>
        <p className="text-sm text-muted-foreground">{t('helperDetail.unavailableDesc', '此外傭目前未開放求職，請稍後再試。')}</p>
        <Button onClick={() => navigate(-1)}>{t('common.back', '返回')}</Button>
      </div>
    );
  }

  // ── helpers ──────────────────────────────────────────────────────────────
  const getDictValue = (dicts: any[], code: string, fallback?: string): string => {
    const dict = dicts.find(d => d.code === code);
    return dict ? getDictDisplayValue(dict) : (fallback || code);
  };
  const getWorkLocation = (loc: string): string => {
    const map: Record<string, string> = {
      HK:    'helperProfileForm.locHongKong',
      SG:    'helperProfileForm.locSingapore',
      TW:    'helperProfileForm.locTaiwan',
      MY:    'helperProfileForm.locMalaysia',
      ME:    'helperProfileForm.locMiddleEast',
      HOME:  'helperProfileForm.locHomeCountry',
      OTHER: 'helperProfileForm.locOther',
    };
    return map[loc] ? t(map[loc], loc) : loc;
  };
  const getCookingLevelLabel = (code: string) => ({
    BEGINNER:     t('cookingLevel.beginner',     '新手'),
    INTERMEDIATE: t('cookingLevel.intermediate', '中階熟手'),
    ADVANCED:     t('cookingLevel.advanced',     '高階專家'),
  }[code] ?? code);

  const parseComma = (s?: string): string[] =>
    s ? s.split(',').map(v => v.trim()).filter(Boolean) : [];

  const formatDate = formatYearMonth;

  const calculateDuration = (startDate: string, endDate?: string): string => {
    const { years: y, months: m } = calcDuration(startDate, endDate);
    if (y > 0 && m > 0) return t('helperDetail.yearsMonths', { y, m });
    if (y > 0) return t('helperDetail.yearsOnly', { y });
    if (m > 0) return t('helperDetail.monthsOnly', { m });
    return t('helperDetail.lessThanOneMonth', '不足一個月');
  };

  const getWorkSkillLabel = (code: string): string => {
    for (const dicts of [workExpSkillDicts, careDicts, houseworkDicts, otherSkillsDicts]) {
      const d = dicts.find((x: any) => x.code === code);
      if (d) return getDictDisplayValue(d);
    }
    return code;
  };

  // ── derived ───────────────────────────────────────────────────────────────
  const canView            = helper.canViewFullDetails ?? false;
  const age                = helper.dateOfBirth ? calculateAge(helper.dateOfBirth) : null;
  const nationalityDisplay = getDictValue(nationalityDicts, helper.nationality, helper.nationality);
  const genderDisplay      = getDictValue(genderDicts, helper.gender, helper.gender);
  const maritalDisplay     = getDictValue(maritalStatusDicts, helper.maritalStatus, helper.maritalStatus);
  const educationDisplay   = helper.educationLevel ? getDictValue(educationLevelDicts, helper.educationLevel, helper.educationLevel) : '';
  const accommodationLabel = helper.accommodation
    ? (getAccommodationOptions(t).find(o => o.value === helper.accommodation)?.label || helper.accommodation)
    : '';

  // 子女歲數由小至大
  const sortAges = (ages: string) =>
    ages.split(',').map(s => s.trim()).filter(Boolean)
      .sort((a, b) => Number(a) - Number(b)).join(',');

  const skills        = parseComma(helper.skills);
  const careTypesList = parseComma(helper.careTypes);
  const houseworkList = parseComma(helper.houseworkTypes);
  const languageList  = parseComma(helper.languageSkills).map(item => {
    const [lang, level] = item.split(':');
    return { lang, level: level || '' };
  });

  // 由近至遠排列（現職優先，其後按開始日期新→舊）
  const workExperiences: WorkExperience[] = [...(helper.workExperiences || [])].sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    return (b.startDate || '').localeCompare(a.startDate || '');
  });
  const hasWorkExperience   = workExperiences.length > 0 || Boolean(helper.workExperience?.trim());
  const hasResponsibilities = careTypesList.length > 0 || houseworkList.length > 0;
  const isOwnProfile = isOwnProfileCheck;

  const salaryText = helper.expectedSalary
    ? `${helper.expectedSalaryCurrency === 'USD' ? 'US$' : 'HK$'}${helper.expectedSalary.toLocaleString()}`
    : t('common.negotiable');

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-6xl">

          {/* Back + Edit */}
          <div className="flex justify-between items-center mb-6">
            {!isOwnProfile && (
              <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4" />
                {t('common.back', '返回')}
              </Button>
            )}
            {isOwnProfile && <div />}
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                showAlert({ type: 'success', message: t('helperDetail.linkCopied', '已複製連結') });
              }}>
                <Share2 className="h-4 w-4" />
              </Button>
              {isOwnProfile && (
                <Button onClick={() => navigate(`/helper/profile/${helper.id}/edit`)}>
                  <Edit className="h-4 w-4 mr-1.5" />
                  {t('common.editProfile')}
                </Button>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">

            {/* ── Left: Profile card (sticky) ── */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card>
                  <CardContent className="p-6 space-y-5">
                    {/* Avatar */}
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative">
                        <div className="w-28 h-28 rounded-2xl overflow-hidden bg-muted ring-4 ring-background shadow-lg">
                          {helper.avatarUrl ? (
                            <img src={helper.avatarUrl} alt={helper.fullName} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-primary/10">
                              <User className="w-14 h-14 text-primary/40" />
                            </div>
                          )}
                        </div>
                        {helper.lookingForJob && (
                          <Badge className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-green-500 text-white border-2 border-background gap-1 shadow-md whitespace-nowrap">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                            {t('helperDetail.canWork', 'Available')}
                          </Badge>
                        )}
                      </div>

                      {/* Name + meta */}
                      <div className="text-center">
                        <h1 className="text-xl font-bold">{helper.fullName}</h1>
                        <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5 mt-1 text-sm text-muted-foreground">
                          {nationalityDisplay && (
                            <span className="flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{nationalityDisplay}</span>
                          )}
                          {genderDisplay && <span>{genderDisplay}</span>}
                          {age !== null
                            ? <span>{age} {t('helperDetail.yrsOld', '歲')}</span>
                            : <span className="flex items-center gap-1 text-primary/70"><Lock className="h-3.5 w-3.5" />{t('helperDetail.age', '年齡')}</span>}
                          {maritalDisplay && <span>{maritalDisplay}</span>}
                        </div>
                        <div className="flex flex-wrap justify-center gap-1 mt-2">

                          {(helper.verifiedDocCount ?? 0) > 0 && (
                            <Badge className="bg-green-500 text-white border-0 gap-1">
                              <Check className="w-3 h-3 stroke-[3]" />
                              {t('helperDetail.verified', '已認證')} {helper.verifiedDocCount}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Key stats */}
                    <div className="space-y-2.5 text-sm">
                      {educationDisplay && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><GraduationCap className="h-4 w-4" />{t('helperDetail.education', '學歷')}</span>
                          <span className="font-medium">{educationDisplay}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Briefcase className="h-4 w-4" />{t('helperDetail.workExperience', '工作經驗')}</span>
                        <span className="font-medium">{helper.experienceYears ?? 0} {t('helperDetail.yrs', '年')}</span>
                      </div>
                      {helper.restDayPreference && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><Clock className="h-4 w-4" />{t('common.restDay')}</span>
                          <span className="font-medium">{
                            helper.restDayPreference === 'ANY' ? t('jobForm.any', '不限') :
                            helper.restDayPreference === 'WEEKDAY' ? t('jobDetail.weekday', '平日') :
                            helper.restDayPreference === 'SATURDAY' ? t('jobDetail.saturday', '星期六') :
                            helper.restDayPreference === 'SUNDAY' ? t('jobDetail.sunday', '星期日') :
                            helper.restDayPreference === 'TO_BE_DISCUSS' ? t('jobForm.salaryNegotiable', '面議') :
                            helper.restDayPreference === 'FLEXIBLE' ? t('jobDetail.flexible', '彈性') :
                            helper.restDayPreference
                          }</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-2"><Activity className="h-4 w-4" />{t('helperDetail.expectedSalary', '期望薪金')}</span>
                        <span className="font-medium text-primary">{salaryText}</span>
                      </div>
                      {helper.availableStartDate && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><Calendar className="h-4 w-4" />{t('common.availableFrom')}</span>
                          <span className="font-medium">{formatDate(helper.availableStartDate)}</span>
                        </div>
                      )}
                      {(helper.height != null || helper.weight != null) && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><Activity className="h-4 w-4" />{t('helperDetail.physique', '身高體重')}</span>
                          <span className="font-medium">
                            {helper.height != null ? `${helper.height}${t('helperDetail.cm', 'cm')}` : ''}
                            {helper.height != null && helper.weight != null ? ' / ' : ''}
                            {helper.weight != null ? `${helper.weight}${t('helperDetail.kg', 'kg')}` : ''}
                          </span>
                        </div>
                      )}
                      {helper.numberOfChildren != null && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><Users className="h-4 w-4" />{t('helperDetail.children', '子女')}</span>
                          <span className="font-medium">
                            {t('helperDetail.childrenCount', { n: helper.numberOfChildren })}{helper.childrenAges ? t('helperDetail.childrenAges', { ages: sortAges(helper.childrenAges) }) : ''}
                          </span>
                        </div>
                      )}
                      {(helper.numberOfSiblings != null || helper.siblingRank != null) && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><Users className="h-4 w-4" />{t('helperDetail.siblings', '兄弟姊妹')}</span>
                          <span className="font-medium">
                            {helper.numberOfSiblings != null ? t('helperDetail.siblingsCount', { n: helper.numberOfSiblings }) : ''}
                            {helper.siblingRank != null ? t('helperDetail.siblingRank', { n: helper.siblingRank }) : ''}
                          </span>
                        </div>
                      )}
                      {helper.spouseOccupation && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><Briefcase className="h-4 w-4" />{t('helperDetail.spouseOccupation', '配偶職業')}</span>
                          <span className="font-medium">{helper.spouseOccupation}</span>
                        </div>
                      )}
                      {helper.religion && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><Sparkles className="h-4 w-4" />{t('common.religion')}</span>
                          <span className="font-medium">{getDictValueByCode(religionDicts, helper.religion)}</span>
                        </div>
                      )}
                      {helper.hometownRegion && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><MapPin className="h-4 w-4" />{t('helperDetail.hometownRegion', '家鄉地區')}</span>
                          <span className="font-medium">{helper.hometownRegion}</span>
                        </div>
                      )}
                      {accommodationLabel && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><Home className="h-4 w-4" />{t('helperDetail.accommodationNeed', '住宿要求')}</span>
                          <span className="font-medium">{accommodationLabel}</span>
                        </div>
                      )}
                      {(helper.currentLocation || helper.lastLoginLocation) && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center gap-2"><MapPin className="h-4 w-4" />{t('helperDetail.currentLocation', '現居地')}</span>
                          <span className="font-medium">{getWorkLocation(helper.currentLocation || helper.lastLoginLocation || '')}</span>
                        </div>
                      )}
                      {(() => {
                        // 登入或資料更新皆視為「活躍」，所有角色可見
                        const activeAt = latestDate(helper.lastLoginAt, helper.updatedAt);
                        return activeAt ? (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground flex items-center gap-2"><Clock className="h-4 w-4" />{t('helperDetail.lastActive', '最後活躍')}</span>
                            <span className="font-medium">{formatRelativeTime(activeAt)}</span>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    <Separator />

                    {/* Actions */}
                    <div className="space-y-2">
                      {/* 面試意向 — EMPLOYER only, not own profile */}
                      {user?.role === 'EMPLOYER' && !isOwnProfile && (() => {
                        const intentStatus = intent?.status;
                        if (intentStatus === 'ACCEPTED') {
                          return (
                            <Button className="w-full gap-2" onClick={handleContactHelper}>
                              {user.isVip
                                ? <><MessageCircle className="h-4 w-4" />{t('helperDetail.contactWhatsapp', '聯絡外傭（WhatsApp）')}</>
                                : <><Crown className="h-4 w-4" />{t('helperDetail.contactUpgradeVip', '聯絡外傭（升級 VIP）')}</>}
                            </Button>
                          );
                        }
                        if (intentStatus === 'PENDING') {
                          return (
                            <Button className="w-full gap-2" disabled variant="secondary">
                              <Send className="h-4 w-4" />
                              {t('helperDetail.waitingReply', '等待外傭回覆…')}
                            </Button>
                          );
                        }
                        if (intentStatus === 'REJECTED') {
                          return (
                            <Button className="w-full gap-2" disabled variant="outline">
                              {t('helperDetail.intentDeclined', '外傭已婉拒此次邀請')}
                            </Button>
                          );
                        }
                        // No intent yet
                        return (
                          <Button className="w-full gap-2" onClick={handleSendIntent} disabled={sendingIntent}>
                            <Send className="h-4 w-4" />
                            {sendingIntent ? t('helperDetail.sending', '發送中…') : t('helperDetail.sendIntent', '發送面試意向')}
                          </Button>
                        );
                      })()}

                      {(!user || user.role === 'EMPLOYER') && !isOwnProfile && (
                        <Button
                          variant={isBookmarked ? 'default' : 'outline'}
                          className="w-full gap-2"
                          onClick={handleToggleBookmark}
                        >
                          <Bookmark className={`h-4 w-4 ${isBookmarked ? 'fill-current' : ''}`} />
                          {isBookmarked ? t('common.bookmarked') : t('helperDetail.bookmarkCandidate', '儲存候選人')}
                        </Button>
                      )}

                      {isOwnProfile && (
                        <Button variant="outline" className="w-full gap-2" onClick={() => navigate(`/helper/profile/${helper.id}/edit`)}>
                          <Edit className="h-4 w-4" />
                          {t('common.editProfile')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* ── Right: Content cards (no tabs) ── */}
            <div className="lg:col-span-2 space-y-6">

              {/* 關於 */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('helperDetail.about', 'About Me')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {(() => {
                      const text = helper.selfIntroduction || t('helperDetail.defaultIntro', { age, nationality: nationalityDisplay, experience: helper.workExperience || t('helperDetail.defaultExperience') });
                      return text.length > 3000 ? `${text.slice(0, 3000)}...` : text;
                    })()}
                  </p>
                </CardContent>
              </Card>

              {/* 語言能力 */}
              {languageList.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('helperDetail.languageAbilities', '語言能力')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {languageList.map((item, i) => (
                        <div key={i} className="flex items-center justify-between rounded-xl bg-muted/60 px-4 py-2.5">
                          <span className="text-sm font-medium">{getDictValue(languageDicts, item.lang, item.lang)}</span>
                          {item.level && (
                            <span className="text-sm text-muted-foreground">{getDictValue(proficiencyDicts, item.level, item.level)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* 技能 + 烹飪 + 工作範疇 (merged) */}
              {(skills.length > 0 || helper.cookingSkillsText || (helper.cookingSkills && helper.cookingSkills.length > 0) || hasResponsibilities) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('helperDetail.skillsAndResponsibilities', '技能與工作範疇')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {skills.length > 0 && (
                      <div>
                        <h4 className="font-medium text-foreground mb-3">{t('common.skills')}</h4>
                        <div className="flex flex-wrap gap-2">
                          {skills.map((skill, i) => (
                            <Badge key={i} variant="outline">{getDictValue(otherSkillsDicts, skill, skill)}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {houseworkList.length > 0 && (
                      <>
                        {skills.length > 0 && <Separator />}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">{t('profile.houseworkTypes', '家務')}</h4>
                          <div className="flex flex-wrap gap-2">
                            {houseworkList.map(h => <Badge key={h} variant="secondary">{getDictValue(houseworkDicts, h, h)}</Badge>)}
                          </div>
                        </div>
                      </>
                    )}
                    {careTypesList.length > 0 && (
                      <>
                        {(skills.length > 0 || houseworkList.length > 0) && <Separator />}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">{t('profile.careTypes', '照顧')}</h4>
                          <div className="flex flex-wrap gap-2">
                            {careTypesList.map(c => <Badge key={c} variant="secondary">{getDictValue(careDicts, c, c)}</Badge>)}
                          </div>
                        </div>
                      </>
                    )}
                    {(helper.cookingSkillsText || (helper.cookingSkills && helper.cookingSkills.length > 0)) && (
                      <>
                        {(skills.length > 0 || houseworkList.length > 0 || careTypesList.length > 0) && <Separator />}
                        <div>
                          <h4 className="font-medium text-foreground mb-3 flex items-center gap-1">{t('common.cookingSkills')}<CookingProficiencyTooltip /></h4>
                          <div className="flex flex-wrap gap-2">
                            {helper.cookingSkillsText
                              ? helper.cookingSkillsText.split(',').filter(Boolean).map((entry, i) => {
                                  const [skillCode, levelCode] = entry.split(':');
                                  const skillLabel = getDictValue(cookingDicts, skillCode, skillCode);
                                  const levelLabel = levelCode ? getCookingLevelLabel(levelCode) : null;
                                  return (
                                    <Badge key={i} variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                      {skillLabel}{levelLabel ? ` (${levelLabel})` : ''}
                                    </Badge>
                                  );
                                })
                              : helper.cookingSkills!.map((skill, i) => (
                                  <Badge key={i} variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                    {getDictValue(cookingDicts, skill, skill)}
                                  </Badge>
                                ))
                            }
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* 工作經驗 */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-lg">{t('helperDetail.workExperience', 'Work Experience')}</CardTitle>
                  {workExperiences.length > 0 && (() => {
                    const total = workExperiences.reduce((sum, exp) => {
                      const s = parseServerDate(exp.startDate), e = exp.endDate ? parseServerDate(exp.endDate) : new Date();
                      return sum + Math.max(0, (e.getFullYear() - s.getFullYear()) * 12 + e.getMonth() - s.getMonth());
                    }, 0);
                    const y = Math.floor(total / 12), m = total % 12;
                    const label = y > 0 && m > 0 ? t('helperDetail.yearsMonths', { y, m }) : y > 0 ? t('helperDetail.yearsOnly', { y }) : t('helperDetail.monthsOnly', { m });
                    return <span className="text-sm font-medium text-primary">{label}</span>;
                  })()}
                </CardHeader>
                <CardContent>
                  {hasWorkExperience ? (
                    workExperiences.length > 0 ? (
                      <div className="relative space-y-4 pl-6 before:absolute before:left-2 before:top-1 before:bottom-1 before:w-px before:bg-border">
                        {workExperiences.map((exp, index) => (
                          <div key={exp.id || index} className="relative">
                            <div className="absolute -left-[1.375rem] top-1.5 w-3 h-3 rounded-full bg-primary border-2 border-background shadow-sm" />
                            <div className="rounded-xl border border-border bg-card p-3 hover:border-primary/30 transition-colors">
                              <div className="flex items-start justify-between gap-2 mb-1.5">
                                <div className="min-w-0">
                                  <h4 className="text-sm font-semibold leading-tight">
                                    {exp.title}
                                    {exp.employerFamily && <span className="text-muted-foreground font-normal"> · {exp.employerFamily}</span>}
                                  </h4>
                                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDate(exp.startDate)} – {exp.isCurrent || !exp.endDate ? t('helperDetail.present', 'Present') : formatDate(exp.endDate)}
                                    <span className="ml-0.5 text-primary font-semibold">({calculateDuration(exp.startDate, exp.endDate)})</span>
                                  </p>
                                </div>
                                {exp.isCurrent && (
                                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-[10px] gap-1 shrink-0">
                                    <Check className="w-2.5 h-2.5 stroke-[3]" /> {t('helperDetail.working', 'Now')}
                                  </Badge>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-1.5 mt-2 text-[11px] text-muted-foreground">
                                {exp.location && (
                                  <span className="flex items-center gap-1 bg-muted px-2 py-0.5 rounded">
                                    <MapPin className="w-3 h-3" /> {getWorkLocation(exp.location)}
                                  </span>
                                )}
                                {exp.unitSizeSqft != null && (
                                  <span className="flex items-center gap-1 bg-muted px-2 py-0.5 rounded">
                                    <Home className="w-3 h-3" /> {t('helperDetail.sqft', { n: exp.unitSizeSqft })}
                                  </span>
                                )}
                                {exp.numberOfEmployers != null && (
                                  <span className="flex items-center gap-1 bg-muted px-2 py-0.5 rounded">
                                    <Users className="w-3 h-3" /> {t('helperDetail.helpersCount', { n: exp.numberOfEmployers })}
                                  </span>
                                )}
                                {exp.familySize && (() => {
                                  const fs: Record<string, number> = exp.familySize as Record<string, number>;
                                  const items = [
                                    { label: t('common.adults'), n: fs.adults || 0, icon: <Users className="w-3 h-3" /> },
                                    { label: t('helperDetail.famNewborn', '初生'), n: fs.newborn || 0, icon: <Baby className="w-3 h-3" /> },
                                    { label: t('helperDetail.famInfant', '嬰兒'), n: fs.infant || fs.babies || 0, icon: <Milk className="w-3 h-3" /> },
                                    { label: t('helperDetail.famChild', '小童'), n: fs.children || 0, icon: <Smile className="w-3 h-3" /> },
                                    { label: t('helperDetail.famTeen', '少年'), n: fs.teen || 0, icon: <GraduationCap className="w-3 h-3" /> },
                                    { label: t('helperDetail.famElderly', '長者'), n: fs.elderly || 0, icon: <Accessibility className="w-3 h-3" /> },
                                    { label: t('helperDetail.famPets', '寵物'), n: fs.pets || 0, icon: <Dog className="w-3 h-3" /> },
                                  ].filter(i => i.n > 0);
                                  return items.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {items.map(i => (
                                        <span key={i.label} className="flex items-center gap-1 bg-muted px-2 py-0.5 rounded text-[11px] text-muted-foreground">
                                          {i.icon} {i.label} {i.n}
                                        </span>
                                      ))}
                                    </div>
                                  ) : null;
                                })()}
                              </div>

                              {exp.skills && exp.skills.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {exp.skills.map((s, i) => (
                                    <Badge key={i} variant="secondary" className="text-[11px]">{getWorkSkillLabel(s)}</Badge>
                                  ))}
                                </div>
                              )}

                              {exp.notes && exp.notes.length > 0 && (
                                <p className="mt-2 text-xs text-foreground/70 leading-relaxed">
                                  {exp.notes.join(' · ')}
                                </p>
                              )}

                              {(() => {
                                const verifiedDocs = (exp.documents ?? []).filter(d => d.verified);
                                const DOC_LABELS: Record<string, string> = {
                                  RECOMMENDATION_LETTER: t('profile.documents.types.RECOMMENDATION_LETTER', '推薦信'),
                                  WORK_PROOF: t('profile.documents.types.WORK_PROOF', '工作證明'),
                                  ID_PROOF: t('profile.documents.types.ID_PROOF', '身份證明'),
                                  EDUCATION_CERT: t('profile.documents.types.EDUCATION_CERT', '學歷證書'),
                                };
                                return verifiedDocs.length > 0 ? (
                                  <div className="mt-2 pt-2 border-t border-border/50 flex flex-wrap gap-1">
                                    {verifiedDocs.map(d => (
                                      <Badge key={d.id} variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px] gap-1">
                                        <Check className="h-3 w-3 stroke-[3]" />
                                        {DOC_LABELS[d.documentType ?? ''] ?? d.documentType}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : exp.hasRecommendationLetter ? (
                                  <div className="mt-2 pt-2 border-t border-border/50">
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px] gap-1">
                                      <Award className="h-3 w-3" />
                                      {t('profile.workExperience.hasRecommendationLetter', 'Recommendation')}
                                    </Badge>
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground whitespace-pre-wrap">{helper.workExperience}</p>
                    )
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('helperDetail.noWorkExperience', 'No work experience records')}</p>
                  )}
                </CardContent>
              </Card>


              {/* 其他資料 */}
              {(helper.willingWeekdayOff != null || helper.eatPork != null || helper.willingCarePets != null ||
                helper.tutoringExperience != null || helper.needReturnHomeNextYear != null ||
                helper.previousAdultAtHome != null || helper.previousCookDaily != null ||
                helper.willingCareNewborn != null || helper.willingCareChild != null ||
                helper.willingCareElderly != null || helper.willingCareDisabled != null) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t('helperDetail.otherInfo', '其他資料')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                      {[
                        { label: t('helperProfileForm.toggleCareNewborn'), val: helper.willingCareNewborn },
                        { label: t('helperProfileForm.toggleCareChild'), val: helper.willingCareChild },
                        { label: t('helperProfileForm.toggleCareElderly'), val: helper.willingCareElderly },
                        { label: t('helperProfileForm.toggleCareDisabled'), val: helper.willingCareDisabled },
                        { label: t('helperProfileForm.toggleCarePets'), val: helper.willingCarePets },
                        { label: t('helperProfileForm.toggleWeekdayOff'), val: helper.willingWeekdayOff },
                        { label: t('helperProfileForm.toggleEatPork'), val: helper.eatPork },
                        { label: t('helperProfileForm.toggleTutoring'), val: helper.tutoringExperience },
                        { label: t('helperProfileForm.togglePrevAdultAtHome'), val: helper.previousAdultAtHome },
                        { label: t('helperProfileForm.togglePrevCookDaily'), val: helper.previousCookDaily },
                        { label: t('helperProfileForm.toggleReturnHomeNextYear'), val: helper.needReturnHomeNextYear },
                      ].filter(item => item.val != null).map(({ label, val }) => (
                        <div key={label} className="flex items-center justify-between border-b border-border/40 py-1.5">
                          <span className="text-muted-foreground text-xs">{label}</span>
                          <span className={`font-medium text-xs ${val ? 'text-green-600' : 'text-red-500'}`}>
                            {val ? t('common.yes', '是') : t('common.no', '否')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
          </div>
        </div>
      </main>

      {/* Mismatch Warning Dialog */}
      {mismatchWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {t('helperDetail.mismatchTitle')}
            </h3>
            <p className="text-sm text-muted-foreground mb-5">{mismatchWarning}</p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setMismatchWarning(null); }}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={() => { setMismatchWarning(null); doContactHelper(); }}>
                {t('helperDetail.confirmContact')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelperDetailPage;
